import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useNavigate } from "react-router-dom";
import useSignupStore from "../../../store/SignUpStore";
import {personalInfoSchema, validatePersonalInfo} from "../../../utils/personalInfoSchema";
import {
    useSignupMutation,
    useVerifyPhoneMutation,
} from "../../../api/SignUpAPI";
import { toast } from "../../../utils/toast";

const PersonalInfoPage = () => {
    const {
        setPersonalInfo,
        marketingOptIn,
        loginId,
        password,
        resetForm,
        setLoading,
        setError,
        error,
        isLoading,
        ...otherSignupData
    } = useSignupStore();
    const navigate = useNavigate();
    const [verificationStatus, setVerificationStatus] = useState("idle");
    const [verificationMessage, setVerificationMessage] = useState("");
    const [remainingTime, setRemainingTime] = useState(180); // 3 minutes in seconds
    const [showCouponPopup, setShowCouponPopup] = useState(false);

    const signupMutation = useSignupMutation();
    const verifyPhoneMutation = useVerifyPhoneMutation();

    const {
        register,
        handleSubmit,
        formState: { errors },
        watch,
    } = useForm({
        resolver: zodResolver(personalInfoSchema),
        mode: "onChange",
    });

    const phoneNumber = watch("phoneNumber");
    const authNumber = watch("authNumber");

    useEffect(() => {
        let timer;
        if (verificationStatus === "sent" && remainingTime > 0) {
            timer = setInterval(() => {
                setRemainingTime((prevTime) => prevTime - 1);
            }, 1000);
        } else if (remainingTime === 0) {
            setVerificationStatus("idle");
        }
        return () => clearInterval(timer);
    }, [verificationStatus, remainingTime]);
    
    const handlePhoneVerification = async () => {
        setVerificationStatus("sending");
        setVerificationMessage("");
        setError(null);

        try {
            const response = await verifyPhoneMutation.mutateAsync({ phoneNumber });
            setVerificationStatus("sent");
            setVerificationMessage(
                "인증번호가 발송되었습니다. 문자메시지를 확인해주세요."
            );
            setRemainingTime(180); // 타이머 3분 설정
        } catch (error) {
            setVerificationMessage("전화번호 인증 요청에 실패했습니다.");
            // setVerificationMessage(error.message);
            setVerificationStatus("idle");
        }
    };

    const onSubmit = async (data) => {
        console.log("Form submitted with data:", data);  // 디버깅을 위한 로그

        const validationResult = validatePersonalInfo(data);
        if (!validationResult.success) {
            console.log("Validation errors:", validationResult.errors);  // 디버깅을 위한 로그
            setError("입력한 정보를 다시 확인해주세요.");
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const signupData = {
                ...otherSignupData,
                ...data,
                loginId,
                password,
                authNumber: data.authNumber,
                marketingOptIn,
                timeStamp: Date.now(),
            };

            console.log("Sending signup data:", signupData);  // 디버깅을 위한 로그

            // 수정 : 백엔드에서 인증번호 확인 결과를 포함한 응답을 처리
            const response = await signupMutation.mutateAsync(signupData);

            // error handling: 백엔드에서 인증번호 확인 실패 시 예외 처리
            if (response.error) {
                setError(response.error);
                // 방금 넘긴 값을 그대로 쓴다. 스토어의 error는 아직 이전 값(대개 null)이라
                // error.message로 접근하면 TypeError가 난다.
                setVerificationMessage(response.error || "회원가입에 실패했습니다. 다시 시도해 주세요.");
                return;
            }
            
            // 회원가입 성공 시 쿠폰 발급 알림 팝업 표시
            setShowCouponPopup(true);
            
            // 3초 후 메인 페이지로 이동
            setTimeout(() => {
                resetForm();
                navigate("/");
            }, 3000);

        } catch (error) {
            toast("회원가입에 실패했습니다. 다시 시도해주세요.");
            setError(
                error.message || "회원가입에 실패했습니다. 다시 시도해 주세요."
            );
            setVerificationMessage("회원가입에 실패했습니다. 다시 시도해 주세요.");
        } finally {
            setLoading(false);
        }
    };

    const formatTime = (seconds) => {
        const minutes = Math.floor(seconds / 60);
        const remainingSeconds = seconds % 60;
        return `${minutes}:${
            remainingSeconds < 10 ? "0" : ""
        }${remainingSeconds}`;
    };
    return (
        <div className="sch-awrap sch-awrap--wide">
            <div className="sch-no">JOIN — 1분이면 끝나요</div>
            <h1>회원가입</h1>

            <form onSubmit={handleSubmit(onSubmit)} className="sch-aform">
                <div className="sch-fsec" style={{ borderTop: 0, paddingTop: 0, marginTop: 0 }}>
                    <span className="sch-t">03 — 개인정보</span>

                    <div className="sch-fgrid2">
                        <label className={`sc-field${errors.name ? " sc-field--error" : ""}`}>
                            <span className="sc-field__label">이름<span className="sc-req">*</span></span>
                            <input {...register("name")} type="text" className="sc-input" placeholder="이름" />
                            {errors.name && <span className="sc-field__error">{errors.name.message}</span>}
                        </label>

                        <label className={`sc-field${errors.birth ? " sc-field--error" : ""}`}>
                            <span className="sc-field__label">생년월일<span className="sc-req">*</span></span>
                            <input {...register("birth")} type="text" className="sc-input" placeholder="YYYYMMDD (8자리)" inputMode="numeric" />
                            {errors.birth && <span className="sc-field__error">{errors.birth.message}</span>}
                        </label>
                    </div>

                    <label className={`sc-field${errors.email ? " sc-field--error" : ""}`}>
                        <span className="sc-field__label">이메일<span className="sc-req">*</span></span>
                        <input {...register("email")} type="email" className="sc-input" placeholder="이메일 입력" />
                        {errors.email && <span className="sc-field__error">{errors.email.message}</span>}
                    </label>

                    <label className={`sc-field${errors.phoneNumber ? " sc-field--error" : ""}`}>
                        <span className="sc-field__label">전화번호<span className="sc-req">*</span></span>
                        <span style={{ display: "flex", gap: "8px" }}>
                            <input
                                {...register("phoneNumber")}
                                type="tel"
                                className="sc-input"
                                placeholder="'-' 없이 숫자만 입력"
                                style={{ flex: 1 }}
                                inputMode="numeric"
                            />
                            <button
                                type="button"
                                className="sch-top__cta"
                                style={{ flex: "none" }}
                                onClick={handlePhoneVerification}
                                disabled={verificationStatus === "sending" || !phoneNumber}
                            >
                                {verificationStatus === "idle" || remainingTime === 0
                                    ? "인증요청"
                                    : verificationStatus === "sending"
                                    ? "요청중…"
                                    : "재요청"}
                            </button>
                        </span>
                        {errors.phoneNumber && <span className="sc-field__error">{errors.phoneNumber.message}</span>}
                    </label>

                    {verificationMessage && (
                        <div
                            className={
                                verificationMessage.includes("실패") ||
                                verificationMessage.includes("이미 가입된 회원입니다.") ||
                                verificationMessage.includes("Unexpected")
                                    ? "sc-alert sc-alert--danger"
                                    : "sc-alert sc-alert--success"
                            }
                        >
                            {verificationMessage}
                        </div>
                    )}

                    {verificationStatus === "sent" && (
                        <label className={`sc-field${errors.authNumber ? " sc-field--error" : ""}`}>
                            <span className="sc-field__label">
                                인증번호<span className="sc-req">*</span>
                                {remainingTime > 0 && (
                                    <span className="sch-timer">남은 시간 {formatTime(remainingTime)}</span>
                                )}
                            </span>
                            <input {...register("authNumber")} type="text" className="sc-input" placeholder="인증번호 입력" inputMode="numeric" />
                            {errors.authNumber && <span className="sc-field__error">{errors.authNumber.message}</span>}
                        </label>
                    )}
                </div>

                {error && <div className="sc-alert sc-alert--danger">{error}</div>}

                <button
                    type="submit"
                    className="sch-bigbtn"
                    style={{ justifyContent: "center", marginTop: "6px" }}
                    disabled={isLoading || !authNumber || verificationStatus !== "sent"}
                >
                    {isLoading ? "처리 중…" : "회원가입"}
                </button>

                <div className="sch-alinks">
                    <button type="button" className="sch-alink" onClick={() => navigate("/signup/account-info")}>
                        ← 계정 정보로 돌아가기
                    </button>
                </div>
            </form>

            {showCouponPopup && (
                <div className="sc-modal-backdrop">
                    <div className="sc-modal">
                        <div className="sc-modal__body" style={{ textAlign: "center", padding: "34px 28px 30px" }}>
                            <div style={{ fontSize: "44px", marginBottom: "10px" }}>🎉</div>
                            <div className="sc-h3" style={{ color: "var(--text)", marginBottom: "10px" }}>
                                회원가입을 축하합니다!
                            </div>
                            <p style={{ margin: 0 }}>
                                가입 축하 쿠폰이 발급되었습니다.<br />
                                마이페이지에서 확인하실 수 있습니다.
                            </p>
                            <p className="sc-caption" style={{ marginTop: "14px" }}>
                                3초 후 자동으로 메인 페이지로 이동합니다.
                            </p>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default PersonalInfoPage;
